import type { ICurrency } from 'v2/components/Pages/Orders/interfaces';
import type { FrequencyType } from 'v2/components/Pages/SubscriptionPlan/Form/constants';

import type { IPaymentStatus } from './invoice';
import type { EComissionType, EProductType } from './product';
import type { EPartnerCode } from 'interfaces/IProduct';

export enum EContractStatus {
  EM_DIA = 1,
  AGUARDANDO_PAGAMENTO = 2,
  SUSPENSO = 3,
  CANCELADO = 4,
  ATRASADO = 7,
  FINALIZADO = 9,
  TRIAL = 10,
  INADIMPLENTE = 11,
}

interface IPaymentMethod {
  id: EContractStatus;
  name: string;
}

export interface ISubscriptions {
  id: number;
  first_invoice_id: number;
  frequency_details: IFrequency;
  payment_charges: number;
  cycles: number;
  card_number: string;
  unlimited_installment: boolean;
  total_price: number;
  unit_price: number;
  amount: number;
  created_at: string;
  updated_at?: string;
  credited_at?: string;
  received_at?: string;
  initial_date: string;
  canceled_at?: string;
  next_charge_date: string;
  charge_email?: string;
  payment_status: IPaymentStatus;
  payment_method: IPaymentMethod;
  producer: IProducer;
  customer: IProducer;
  affiliate: IAffiliate;
  product: IProduct;
  currency?: string;
}

export interface ISubscriptionInvoice {
  invoice_id: number;
  id: number;
  payment_status: IPaymentStatus;
  payment_method: IPaymentMethod;
  installments: number;
  chargeback: boolean;
  recovery: boolean;
  created_at: Date;
  expired_at: any;
  refunded_at: any;
  transaction_value: number;
  quotation_value: number;
  receive_at: any;
  order?: number;
  sale_value: number;
  rate_amount: number;
  comission_amount: number;
  partner_amount: number;
  amount: number;
  anticipation_amount: number;
  refunded_amount: number;
  net_income: number;
  others_values: number;
  credited_at: string;
  producer_id: number;
  received_at: string;
  commission_amount: number;
  checkout_recovery_link: any;
  boleto_recovery_link: any;
  card_brand: string;
  affiliate: IAffiliate;
  product: IProduct;
  bankslip_url: string;
}

export interface IProduct {
  id: number;
  title: string;
  allows_card: number;
  image: string;
  producer_id: number;
  sku: string;
}

interface IAffiliate {
  id: number;
  email?: string;
  name?: string;
}

interface IProducer {
  id: number;
  name: string;
  business_name: string;
  email: string;
  celphone: string;
}

interface IFrequency {
  frequency: string;
  frequency_type: string;
  frequency_reference: number;
  total_cycles: string;
  number_of_charges: string;
}

export interface ISubscriptionsStatus {
  id: number;
  value: string;
  label: string;
}

export interface ICancelReason {
  id: number;
  name: string;
  status: number;
  created_at: string;
  updated_atd: string;
}

export type SubscriptionPlan = {
  active: number;
  affiliates: number;
  affiliate_instructions: string;
  affiliation_contract: string;
  affiliation_status: string;
  allows_boleto: boolean;
  allows_card: boolean;
  allows_downgrade: boolean;
  allows_eduzz_balance: boolean;
  allows_multiple_cards: boolean;
  allows_paypal: boolean;
  allows_pix: boolean;
  allows_upgrade: boolean;
  allow_affiliation: boolean;
  allows_plan_change_for_product: boolean;
  allows_plan_change_for_variation: boolean;
  alternative_url: string;
  ask_address_checkout: boolean;
  ask_address_thanks: boolean;
  author_name: string;
  auto_affiliation: boolean;
  auto_refund: boolean;
  can_create_pixel?: boolean;
  categories: string[];
  charge_type: string;
  clients: number;
  cnt_partner_cod: EPartnerCode;
  comission_on_first_invoice: boolean;
  comission_type: string;
  comission_value: number;
  comission_value_with_fee: number;
  connects: number;
  currency: ICurrency[];
  deliver_first_charge: boolean;
  plan_description: string;
  details: string;
  document_on_checkout: boolean;
  email_welcome: boolean;
  expiration_date: string;
  friendly_name: string;
  funnel_commission: number;
  has_children: boolean;
  has_expiration_date: boolean;
  hide_quantity: boolean;
  id: number;
  image: string;
  image_id: number;
  infinity_funnel: boolean;
  interest_free_installment: boolean;
  invite_url: string;
  is_producer: boolean;
  launch_date: string;
  limit_charges: number;
  limit_purchase: number;
  marketplace: boolean;
  max_comission_value: number;
  max_price: number;
  membership_comission_type: string;
  membership_comission_value: number;
  membership_fee: boolean;
  membership_price: number;
  moderation_status: string;
  name_extract_description: string;
  new_value_after_expiration_date: number;
  notify_stock: number;
  overdue_limit: number;
  parent_product: number;
  permanent_access: boolean;
  prelaunch: boolean;
  price: number;
  private_label_rights: number;
  producer_id: number;
  program_content: string;
  recovery_email: string;
  recruit: boolean;
  recruit_commission_type: string;
  recruit_commission_value: number;
  recruit_max_sales: number;
  recurring_comission_type: string;
  recurring_comission_value: number;
  recurring_comission_value_with_fee: number;
  recurring_days_start_charging: number;
  recurring_frequency: number;
  recurring_frequency_type: string;
  recurring_start_charging: string;
  recurring_value: number;
  refund_days: number;
  remove_access: boolean;
  rpc_affiliate: number;
  rpc_producer: number;
  sales_expectation: number;
  sales_recovery: boolean;
  send_billing_reminder: boolean;
  sku?: string;
  status: 'inactive' | 'active' | 'filed' | 'draft' | 'invalid';
  stock: number;
  support_email: string;
  thankyou_type: number;
  plan_title: string;
  todo: number;
  total_gains: number;
  total_sales: number;
  trial: boolean;
  trial_days: number;
  type: EProductType;
  undetermined_charges: boolean;
  unlimited_installment: boolean;
  url_checkout: string;
  url_thankyou_boleto: string;
  url_thankyou_card: string;
  value_expiration_date: string;
  visible: boolean;
  webhook: boolean;
  webhook_url: string;
  yearly_installment: boolean;
  prices: SubscriptionPlanPrice[];
  plan_changeconfig: PlanChangeConfig;
};

export type PlanChangeConfig = Array<{
  plan_id: number;
  plan_title: string;
  plan_desc: string;
  product_id: number;
  product_name: string;
  base_price: number;
  selected: boolean;
  variation: boolean;
}>;

export type SubscriptionPlanPrice = {
  value: number;
  currency_code: string;
  membership_value: number;
};

export type CreateSubscriptionPlanPayload = {
  allows_card: boolean;
  allows_downgrade: boolean;
  allows_upgrade: boolean;
  comission_on_first_invoice: boolean;
  currency_id: number;
  deliver_first_charge: boolean;
  limit_charges: number;
  membership_comission_type: EComissionType;
  membership_comission_value: number;
  membership_fee: boolean;
  membership_price: number;
  overdue_limit: number;
  permanent_access: boolean;
  plan_description: string;
  plan_title: string;
  prices: SubscriptionPlanPrice[];
  recurring_comission_type: EComissionType;
  recurring_comission_value: number;
  recurring_frequency: number;
  recurring_frequency_type: FrequencyType;
  recurring_value: number;
  remove_access: boolean;
  send_billing_reminder: boolean;
  trial: boolean;
  trial_days: number;
  undetermined_charges: boolean;
  yearly_installment: boolean;
  plan_changeconfig: PlanChangeConfig;
};

export type UpdateSubscriptionPlanPayload = {
  allows_card: boolean;
  allows_downgrade: boolean;
  allows_upgrade: boolean;
  comission_on_first_invoice: boolean;
  currency_id: number;
  deliver_first_charge: boolean;
  limit_charges: number;
  membership_comission_type: EComissionType;
  membership_comission_value: number;
  membership_fee: boolean;
  membership_price: number;
  overdue_limit: number;
  permanent_access: boolean;
  plan_description: string;
  plan_title: string;
  prices: SubscriptionPlanPrice[];
  recurring_comission_type: EComissionType;
  recurring_comission_value: number;
  recurring_frequency: number;
  recurring_frequency_type: FrequencyType;
  recurring_value: number;
  remove_access: boolean;
  send_billing_reminder: boolean;
  trial: boolean;
  trial_days: number;
  undetermined_charges: boolean;
  yearly_installment: boolean;
};

export enum EDateTypes {
  CREATED = 'created_at',
  CANCELED = 'canceled_at',
}

export enum ECustomerSearchTypes {
  NAME = 'customer_name',
  EMAIL = 'customer_email',
  DOCUMENT = 'customer_document',
}

const PaymentTypes = [
  { id: 2, label: 'Boleto', value: 'boleto' },
  { id: 1, label: 'Cartão de crédito', value: 'credit' },
];

export interface ISubscriptionsFilter {
  date_type: EDateTypes;
  date: string[];
  customer_type?: string;
  customer?: string;
  invoice_id?: string[];
  status?: string[];
  product_id?: any[];
  payment_method: (typeof PaymentTypes)[];
  affiliate_id?: [];
  specific_filters?: [];
  id?: any | any[];
  type?: any[];
  export?: number;
  negotiated_invoices?: boolean;
  unlimited_installment?: boolean;
  open_invoices?: boolean;
  advanced?: boolean;
}

export enum ResolveSubscriptionChangeStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING = 'pending',
  EXPIRED = 'expired',
}

export enum ResolveSubscriptionChangeStatusLabel {
  APPROVED = 'Aprovado',
  REJECTED = 'Rejeitado',
  PENDING = 'Pendente',
  EXPIRED = 'Expirado',
}

export interface ResolveSubscriptionChangeResponse {
  data: {
    status: Exclude<ResolveSubscriptionChangeStatus, ResolveSubscriptionChangeStatus.PENDING>;
    statusLabel: ResolveSubscriptionChangeStatusLabel;
    message: string;
  };
}
